import { Drawer, useMediaQuery, useTheme, Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LogoutIcon from '@mui/icons-material/Logout'
import { useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import kycIconSvg from 'src/assets/kyc approval.svg'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { MentorSetupContext } from 'src/mentor/context/MentorSetupContext'
import { preloadImage } from 'src/utils/imagePreloader'
import { logoutHandlerMentor } from 'src/mentor/helpers/utilityFunctions'
import { ProfileDataComponent } from '../MentorProfilePage'
import { mentorProfileSaveFunction } from '../MentorProfilePage/utils'
import {
  KycIcon,
  KycProceedButton,
  KycExploreAppButton,
  KycSubText,
  KycText,
} from '../../layout/Styles'

export default function KycPendingActionsPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { mentorProfileData, updateProfileData, allLegalPolicies, setDontShowKycModal, refreshLegalPolicies } =
    useContext(MentorProfileContext)
  const { allTags } = useContext(MentorSetupContext)
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false)
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)

  const handleEditProfile = () => {
    setProfileDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setProfileDrawerOpen(false)
  }

  const handleExploreApp = () => {
    // Set flags to indicate user explicitly chose consumer portal
    sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
    sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
    sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
    // Navigate to consumer portal (root path)
    window.location.replace('/')
  }

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true)
  }

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false)
    logoutHandlerMentor()
    navigate('/')
  }

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false)
  }

  // Preload KYC SVG immediately on mount for LCP optimization
  useEffect(() => {
    if (kycIconSvg && typeof kycIconSvg === 'string') {
      preloadImage(kycIconSvg, {
        as: 'image',
        fetchpriority: 'high',
      })
    }
  }, [])

  return (
    <>
      {/* Use simple Box instead of DialogContent for better LCP performance */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: { xs: 2, sm: 4 },
          gap: '40px',
          position: 'relative',
        }}
      >
        {/* Logout Button - Top Right */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '0.75rem', sm: '1rem' },
            right: { xs: '0.75rem', sm: '1rem' },
          }}
        >
          <Tooltip title="Logout" arrow>
            <IconButton
              onClick={handleLogoutClick}
              sx={{
                color: '#ef4444',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                },
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
              aria-label="Logout"
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <KycIcon
          src={kycIconSvg}
          alt="KYC Approval Pending"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          width="100"
          height="100"
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <KycText>KYC Approval Pending</KycText>
          <KycSubText>
            Your KYC approval is pending. We will send you an email when it is approved.
            <br />
            In the meantime, you can edit your profile.
          </KycSubText>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: '24px',
            alignItems: 'center',
          }}
        >
          <KycProceedButton onClick={handleEditProfile}>Edit Profile</KycProceedButton>
          <KycExploreAppButton onClick={handleExploreApp}>
            Explore Mental Wellness App
          </KycExploreAppButton>
        </Box>
      </Box>

      {/* Profile Drawer - Right to left on desktop, bottom to top on mobile */}
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={profileDrawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : { xs: '100%', sm: '600px', md: '700px', lg: '800px' },
            height: isMobile ? '90vh' : '100vh',
            borderRadius: isMobile ? '16px 16px 0 0' : '0',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
      >
        {/* Drawer Header with Close Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <KycText sx={{ fontSize: '18px', fontWeight: 600 }}>Edit Profile</KycText>
          <IconButton onClick={handleCloseDrawer} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Profile Form Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <ProfileDataComponent
            profileData={mentorProfileData}
            updateProfileData={updateProfileData}
            enteringFirstTime={false}
            allLegalPolicies={allLegalPolicies}
            setDontShowKycModal={setDontShowKycModal}
            refreshLegalPolicies={refreshLegalPolicies}
            formContextData={{ domains: allTags }}
            slugInitialUrl={'https://aumhum.com/mentor/'}
            saveObjectTransform={(formData) => {
              // After save, the ProfileDataComponent will call updateProfileData
              // and then onCancel, which will close the drawer
              return mentorProfileSaveFunction(formData)
            }}
            onCancel={handleCloseDrawer}
          />
        </Box>
      </Drawer>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Logout
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to logout? You will need to sign in again to access your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button
            onClick={handleLogoutCancel}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

